import * as http from 'node:http';
import * as https from 'node:https';
import type { IncomingMessage, ServerResponse } from 'node:http';

/**
 * Proxy Error Handler 选项
 */
export interface ProxyErrorOptions {
  /** 等待服务重启的超时时间（毫秒），默认 5000ms */
  retryTimeout?: number;
  /** 轮询检查服务的间隔时间（毫秒），默认 500ms */
  retryInterval?: number;
  /** 目标服务器地址，用于检查服务是否恢复，格式：http://localhost:3000 */
  target?: string;
}

/**
 * 判断是否是连接错误（服务未启动或重启中）
 */
function isConnectionError(err: Error): boolean {
  const code = (err as NodeJS.ErrnoException).code;
  const connectionErrorCodes = ['ECONNREFUSED', 'ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND', 'ENETUNREACH'];
  return connectionErrorCodes.includes(code || '');
}

/**
 * 检查服务是否可用（通过 HTTP 请求）
 */
function checkServiceAvailable(targetUrl: string, timeout = 2000): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const url = new URL(targetUrl);
      const isHttps = url.protocol === 'https:';
      const httpModule = isHttps ? https : http;

      const req = httpModule.request(
        {
          hostname: url.hostname,
          port: url.port || (isHttps ? 443 : 80),
          path: '/',
          method: 'HEAD',
          timeout,
        },
        (res) => {
          // 任何非 502 响应都表示服务可用
          resolve(res.statusCode !== 502);
        }
      );

      req.on('timeout', () => {
        req.destroy();
        resolve(false);
      });

      req.on('error', () => {
        resolve(false);
      });

      req.end();
    } catch {
      resolve(false);
    }
  });
}

/**
 * 等待服务恢复，带超时和轮询检查
 */
async function waitForServiceRecovery(
  targetUrl: string,
  timeout: number,
  interval: number
): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const isAvailable = await checkServiceAvailable(targetUrl, 2000);
    if (isAvailable) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }

  return false;
}

/**
 * 发送 502 JSON 响应
 */
function send502Json(res: ServerResponse): void {
  if (res.headersSent) return;

  res.writeHead(502, {
    'Content-Type': 'application/json; charset=utf-8',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
  });
  res.end(JSON.stringify({
    error: 'Bad Gateway',
    message: 'Backend service is temporarily unavailable',
  }));
}

/**
 * 发送 302 重定向响应
 */
function sendRedirect(req: IncomingMessage, res: ServerResponse): void {
  if (res.headersSent) return;

  const originalUrl = req.url || '/';
  console.log('[Proxy] Service recovered, redirecting to', originalUrl);

  res.writeHead(302, {
    'Location': originalUrl,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
  });
  res.end();
}

/**
 * HTTP Proxy 错误处理器
 *
 * 处理策略：
 * 1. 连接错误（服务重启中）→ 等待 5s，恢复则 302 重定向，否则 502
 * 2. 其他错误 → 直接 502
 *
 * @example
 * ```typescript
 * import { createProxyMiddleware } from 'http-proxy-middleware';
 * import { handleDevProxyError } from '@lark-apaas/devtool-kits';
 *
 * const proxy = createProxyMiddleware({
 *   target: 'http://localhost:3000',
 *   onError: (err, req, res) => {
 *     handleDevProxyError(err, req, res, {
 *       retryTimeout: 5000,
 *       retryInterval: 500,
 *     });
 *   }
 * });
 * ```
 */
export function handleDevProxyError(
  err: Error,
  req: IncomingMessage,
  res: ServerResponse,
  options?: ProxyErrorOptions
): void {
  const {
    retryTimeout = 5000,
    retryInterval = 500,
    target = `http://localhost:${process.env.SERVER_PORT || 3000}`,
  } = options || {};

  console.error('[Proxy] Error:', err.message);

  if (res.headersSent) {
    console.error('[Proxy] Headers already sent, cannot handle error');
    return;
  }

  // 非连接错误，直接返回 502
  if (!isConnectionError(err)) {
    console.log('[Proxy] Non-connection error, returning 502');
    send502Json(res);
    return;
  }

  // 连接错误，可能是服务热重载中，等待恢复
  console.log('[Proxy] Connection error, waiting for service recovery...');

  (async () => {
    try {
      const recovered = await waitForServiceRecovery(target, retryTimeout, retryInterval);

      if (recovered) {
        sendRedirect(req, res);
      } else {
        console.log('[Proxy] Service did not recover within timeout, returning 502');
        send502Json(res);
      }
    } catch (error) {
      console.error('[Proxy] Error during recovery wait:', error);
      send502Json(res);
    }
  })();
}
